import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Empty, Input, Popconfirm, Spin, Tag, Typography, message } from "antd";
import { DeleteOutlined, SendOutlined } from "@ant-design/icons";
import {
    deleteVideoReviewComment as deletePublicVideoReviewComment,
    fetchVideoReviewComments,
    postVideoReviewComment,
} from "../../utils/videoReviewApi";
import {
    deleteVideoReviewComment as deleteAdminVideoReviewComment,
    listVideoReviewComments,
} from "../../utils/videoReviewAdminApi";
import { formatVideoTimestamp } from "../../utils/videoReviewFormat";
import { useTranslation } from "../../i18n";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

function sortComments(items) {
    return [...items].sort(
        (a, b) => a.timestampMs - b.timestampMs || (a.createdAt || 0) - (b.createdAt || 0)
    );
}

function VideoReviewComments({
    linkId,
    reviewerName,
    playerRef,
    currentTimeMs,
    commentVisibility,
    composerInputRef,
    adminMode = false,
}) {
    const { t } = useTranslation();
    const localInputRef = useRef(null);
    const inputRef = composerInputRef ?? localInputRef;
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [body, setBody] = useState("");
    const [highlightId, setHighlightId] = useState(null);

    const loadComments = useCallback(
        async ({ silent = false } = {}) => {
            if (!silent) {
                setLoading(true);
            }
            try {
                const items = adminMode
                    ? await listVideoReviewComments(linkId)
                    : await fetchVideoReviewComments({ linkId, reviewerName });
                setComments(sortComments(items));
            } catch {
                if (!silent) {
                    message.error(t("videoReview.comments.loadFailed"));
                }
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [adminMode, linkId, reviewerName, t]
    );

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    const handleSeek = useCallback(
        (comment) => {
            playerRef.current?.seekTo(comment.timestampMs);
            setHighlightId(comment.id);
            window.setTimeout(() => setHighlightId(null), 1200);
        },
        [playerRef]
    );

    const handleSubmit = useCallback(async () => {
        const trimmed = body.trim();
        if (!trimmed) {
            return;
        }
        const timestampMs = playerRef.current?.getCurrentTimeMs() ?? currentTimeMs;
        setSubmitting(true);
        try {
            const created = await postVideoReviewComment({
                linkId,
                reviewerName,
                timestampMs,
                body: trimmed,
            });
            setBody("");
            message.success(t("videoReview.comments.posted"));
            if (created) {
                setComments((prev) => sortComments([...prev, created]));
                setHighlightId(created.id);
                window.setTimeout(() => setHighlightId(null), 1200);
            } else {
                await loadComments({ silent: true });
            }
        } catch (error) {
            if (error?.code === "EXPIRED") {
                message.error(t("videoReview.comments.expired"));
            } else {
                message.error(t("videoReview.comments.postFailed"));
            }
        } finally {
            setSubmitting(false);
        }
    }, [body, currentTimeMs, linkId, loadComments, playerRef, reviewerName, t]);

    const handleDelete = useCallback(
        async (comment) => {
            setDeletingId(comment.id);
            try {
                if (adminMode) {
                    await deleteAdminVideoReviewComment({
                        commentId: comment.id,
                        linkId,
                    });
                } else {
                    await deletePublicVideoReviewComment({
                        commentId: comment.id,
                        linkId,
                        reviewerName,
                    });
                }
                setComments((prev) => prev.filter((item) => item.id !== comment.id));
                message.success(t("videoReview.comments.deleted"));
            } catch (error) {
                if (!adminMode && error?.code === "EXPIRED") {
                    message.error(t("videoReview.comments.expired"));
                } else {
                    message.error(t("videoReview.comments.deleteFailed"));
                }
            } finally {
                setDeletingId(null);
            }
        },
        [adminMode, linkId, reviewerName, t]
    );

    return (
        <div className="video-review-comments">
            <div className="video-review-comments__header">
                <Text strong>{t("videoReview.comments.title")}</Text>
                {adminMode ? (
                    <Tag color="blue">{t("videoReview.audit.allCommentsHint")}</Tag>
                ) : (
                    commentVisibility === "individual" && (
                        <Tag>{t("videoReview.comments.individualHint")}</Tag>
                    )
                )}
            </div>

            <div className="video-review-comments__list">
                {loading ? (
                    <div className="video-review-comments__loading">
                        <Spin />
                    </div>
                ) : comments.length === 0 ? (
                    <Empty description={t("videoReview.comments.empty")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    comments.map((comment) => (
                        <div
                            key={comment.id}
                            className={`video-review-comments__item${
                                highlightId === comment.id ? " video-review-comments__item--highlight" : ""
                            }`}
                        >
                            <button
                                type="button"
                                className="video-review-comments__item-main"
                                onClick={() => handleSeek(comment)}
                            >
                                <span className="video-review-comments__time">
                                    {formatVideoTimestamp(comment.timestampMs)}
                                </span>
                                <span className="video-review-comments__author">{comment.reviewerName}</span>
                                <Paragraph className="video-review-comments__body">{comment.body}</Paragraph>
                            </button>
                            {(adminMode || comment.reviewerName === reviewerName) && (
                                <Popconfirm
                                    title={t("videoReview.comments.deleteConfirm")}
                                    okText={t("videoReview.admin.deleteOk")}
                                    cancelText={t("videoReview.admin.deleteCancel")}
                                    onConfirm={() => handleDelete(comment)}
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={deletingId === comment.id}
                                        aria-label={t("videoReview.comments.delete")}
                                        className="video-review-comments__delete"
                                    />
                                </Popconfirm>
                            )}
                        </div>
                    ))
                )}
            </div>

            {!adminMode && (
                <div className="video-review-comments__composer">
                    <div className="video-review-comments__at">
                        {t("videoReview.comments.atTime", {
                            time: formatVideoTimestamp(currentTimeMs),
                        })}
                    </div>
                    <TextArea
                        ref={inputRef}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={t("videoReview.comments.placeholder")}
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        maxLength={2000}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={submitting}
                        disabled={!body.trim()}
                        onClick={handleSubmit}
                        block
                    >
                        {t("videoReview.comments.submit")}
                    </Button>
                </div>
            )}
        </div>
    );
}

export default VideoReviewComments;
